import { useState } from 'react';
import { UserRow } from '../components/UserRow';
import { RequestCard } from '../components/RequestCard';
import { AuditLogTable } from '../components/admin/AuditLogTable';
import { DetailsCard } from '../components/app/DeployFormCards';
import { DeployStepper } from '../components/app/DeployStepper';
import { COLORS, FONTS } from '../styles/tokens';
import { useTheme } from '../hooks/useTheme';
import type { KeycloakRole, KeycloakUser } from '../api/keycloak-admin';
import type { AuditLogRow } from '../lib/notifications-api';

const SAMPLE_USER: KeycloakUser = {
  id: 'u1', username: 'jdoe', email: 'jordan.doe@velocityexplorations.com',
  firstName: 'Jordan', lastName: 'Doe', enabled: true,
  createdTimestamp: Date.now() - 86_400_000, emailVerified: true,
};

const SAMPLE_ROLES: KeycloakRole[] = [
  { id: 'r1', name: 'app:cadence', description: 'Cadence' },
  { id: 'r2', name: 'app:cert', description: 'Cert' },
];

const SAMPLE_AUDIT: AuditLogRow[] = [
  {
    id: 'a1', app_id: '1', app_slug: 'cadence', app_name: 'Cadence',
    event_type: 'deploy', from_version: 3, to_version: 4,
    actor_sub: 'u1', actor_email: 'jordan.doe@velocityexplorations.com',
    metadata: {}, created_at: new Date().toISOString(),
  },
  {
    id: 'a2', app_id: '2', app_slug: 'cert', app_name: 'Cert',
    event_type: 'rollback', from_version: 2, to_version: 1,
    actor_sub: 'u1', actor_email: 'preview@vertex.local',
    metadata: {}, created_at: new Date().toISOString(),
  },
];

/** Shared heading used by the Keycloak-free phone preview pages. */
function PreviewTitle({ label }: { label: string }): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  return (
    <h2 className="vx-track" style={{
      fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em',
      color: colors.text, textTransform: 'uppercase', margin: '0 0 16px',
    }}>
      {label}
    </h2>
  );
}

/** User Management list with sample rows. */
export function PreviewUsers(): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
        <PreviewTitle label="User Management" />
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input className="vx-search-field" placeholder="Search users" readOnly style={{
            height: '34px', padding: '0 12px', borderRadius: '6px', width: '190px',
            border: `1px solid ${colors.border}`, backgroundColor: colors.card,
            color: colors.text, fontFamily: FONTS.body, fontSize: '12px',
          }} />
          <select className="vx-full-field" aria-label="Filter by app" style={{
            height: '34px', padding: '0 10px', borderRadius: '6px',
            border: `1px solid ${colors.border}`, backgroundColor: colors.card, color: colors.text,
          }}>
            <option>All apps</option>
          </select>
        </div>
      </div>
      <div style={{ backgroundColor: colors.card, borderRadius: '12px', overflow: 'hidden' }}>
        <UserRow user={SAMPLE_USER} isDark={isDark} colors={colors} roles={['admin', 'app:cadence']} isProcessing={false} onManageApps={() => undefined} onEdit={() => undefined} />
        <UserRow user={{ ...SAMPLE_USER, id: 'u2', firstName: 'Alex', lastName: 'Rivera', email: 'alex.rivera@velocityexplorations.com', username: 'arivera' }} isDark={isDark} colors={colors} roles={['app:cert']} isProcessing={false} onManageApps={() => undefined} onEdit={() => undefined} />
      </div>
    </div>
  );
}

/** Account Requests card stack. */
export function PreviewRequests(): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const [open, setOpen] = useState(false);
  return (
    <div className="vx-page-extra-pad">
      <PreviewTitle label="Account Requests" />
      <RequestCard
        request={{ user: SAMPLE_USER, roles: [] }}
        appRoles={SAMPLE_ROLES}
        selectedRoles={new Set(['app:cadence'])}
        onToggleRole={() => undefined}
        isDropdownOpen={open}
        onDropdownOpenChange={setOpen}
        actionInProgress={false}
        onApprove={() => undefined}
        onDeny={() => undefined}
        isDark={isDark}
        colors={colors}
      />
    </div>
  );
}

/** Organizations setup bar plus stacked columns. */
export function PreviewOrgs(): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
        <PreviewTitle label="Organizations" />
        <button type="button" style={{ height: '34px', padding: '0 16px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.text, fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          Save Changes
        </button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
        <select className="vx-full-field" aria-label="Sector" style={{ height: '36px', width: '180px', borderRadius: '6px', border: `1px solid ${colors.border}`, backgroundColor: colors.card, color: colors.text }}>
          <option>DoD</option>
        </select>
        <input className="vx-full-field" readOnly value="Space Launch Delta 45" style={{ height: '36px', flex: 1, minWidth: '200px', padding: '0 12px', borderRadius: '6px', border: `1px solid ${colors.border}`, backgroundColor: colors.card, color: colors.text }} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
        <div className="vx-fill-col" style={{ flex: '1 1 400px', minWidth: '300px', padding: '16px', borderRadius: '10px', border: `1px solid ${colors.border}` }}>
          <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.text }}>Space Launch Delta 45</p>
          <p style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary, marginTop: '8px' }}>Range Operations</p>
        </div>
        <div className="vx-fill-col" style={{ flex: '1 1 320px', minWidth: '280px', padding: '16px', borderRadius: '10px', border: `1px solid ${colors.border}` }}>
          <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary }}>Select a unit to edit</p>
        </div>
      </div>
    </div>
  );
}

/** Templates list plus preview pane. */
export function PreviewTemplates(): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
        <PreviewTitle label="Templates" />
        <button type="button" style={{ height: '34px', padding: '0 16px', borderRadius: '6px', border: 'none', background: 'linear-gradient(90deg, #0266C6, #00407D)', color: '#FFF', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          + New Template
        </button>
      </div>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <div className="vx-fill-col" style={{ flex: '1 1 400px', minWidth: '300px', padding: '16px', borderRadius: '10px', border: `1px solid ${colors.border}` }}>
          <p style={{ fontFamily: FONTS.heading, fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textSecondary }}>DoD</p>
          <p style={{ fontFamily: FONTS.body, fontSize: '14px', color: colors.text, marginTop: '8px' }}>Space Launch Delta 45</p>
        </div>
        <div className="vx-fill-col" style={{ flex: '1 1 320px', minWidth: '280px', padding: '16px', borderRadius: '10px', border: `1px solid ${colors.border}` }}>
          <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.text }}>Preview: Space Launch Delta 45</p>
        </div>
      </div>
    </div>
  );
}

/** Settings security stat cards. */
export function PreviewSettings(): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '20px' }}>
        <nav className="vx-tab-scroller" style={{ display: 'flex', gap: '4px' }}>
          <span style={{ padding: '6px 14px', borderRadius: '6px', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FFF', backgroundColor: 'rgba(1, 169, 237, 0.15)' }}>Security</span>
          <span style={{ padding: '6px 14px', borderRadius: '6px', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textSecondary }}>Audit Log</span>
        </nav>
      </div>
      <h2 className="vx-track" style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: '0 0 20px' }}>
        Security Dashboard
      </h2>
      <div className="vx-stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
        {['Total Users', 'Active Users', 'Disabled Users', 'Failed Logins'].map(label => (
          <div key={label} style={{ backgroundColor: colors.card, borderRadius: '12px', padding: '20px' }}>
            <p style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>{label}</p>
            <p style={{ fontFamily: FONTS.heading, fontSize: '28px', color: colors.text, margin: '8px 0 0' }}>12</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** TAK admin toolbar and device list. */
export function PreviewTak(): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
        <PreviewTitle label="TAK Server Admin" />
        <input className="vx-search-field" placeholder="Search..." readOnly style={{
          height: '36px', width: '200px', padding: '0 12px', borderRadius: '6px',
          border: `1px solid ${colors.border}`, backgroundColor: colors.card, color: colors.text,
        }} />
      </div>
      <div className="vx-tab-scroller" style={{ display: 'flex', gap: '4px', marginBottom: '20px' }}>
        {['Overview', 'Devices', 'Groups', 'Missions'].map((tab, i) => (
          <span key={tab} style={{ padding: '6px 14px', borderRadius: '6px', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: i === 1 ? '#FFF' : colors.textSecondary, backgroundColor: i === 1 ? 'rgba(1, 169, 237, 0.15)' : 'transparent' }}>{tab}</span>
        ))}
      </div>
      <div style={{ borderRadius: '8px', backgroundColor: colors.card, border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
        <div className="vx-stack-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1.5fr 1fr 1fr', gap: '12px', padding: '14px 20px' }}>
          <span className="vx-labeled-cell" data-label="Callsign" style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.text }}>VIPER-1</span>
          <span className="vx-labeled-cell" data-label="UID" style={{ fontFamily: 'monospace', fontSize: '12px', color: colors.textSecondary }}>ANDROID-9f2c…</span>
          <span className="vx-labeled-cell" data-label="Last Seen" style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary }}>2m ago</span>
          <span className="vx-labeled-cell" data-label="Status" style={{ fontFamily: FONTS.body, fontSize: '12px', color: '#4ADE80' }}>Online</span>
          <span className="vx-labeled-cell" data-label="Team" style={{ fontFamily: FONTS.body, fontSize: '12px', color: '#00BCD4' }}>Cyan</span>
        </div>
      </div>
    </div>
  );
}

/** Deploy form first step. */
export function PreviewDeploy(): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  return (
    <div style={{ maxWidth: '720px', margin: '0 auto' }}>
      <div className="vx-page-toolbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', gap: '12px' }}>
        <PreviewTitle label="Deploy a New App" />
      </div>
      <DeployStepper currentStep="name" textPrimary={colors.text} textSecondary={colors.textSecondary} accent="#01A9ED" border={colors.border} />
      <DetailsCard
        colors={colors} isDark={isDark}
        name="Hello World" setName={() => undefined}
        description="" setDescription={() => undefined}
        team="Vertex" setTeam={() => undefined}
        category="User Apps" setCategory={() => undefined}
        icon={null} setIcon={() => undefined}
        onNext={() => undefined}
      />
    </div>
  );
}

/** Deployed apps mobile card list. */
export function PreviewDeployed(): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
        <PreviewTitle label="Deployed Apps" />
        <button type="button" style={{ height: '34px', padding: '0 16px', borderRadius: '6px', border: 'none', background: 'linear-gradient(90deg, #0266C6, #00407D)', color: '#FFF', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          Deploy New
        </button>
      </div>
      <div className="vx-mobile-only" style={{ borderRadius: '10px', border: `1px solid ${colors.border}` }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ fontFamily: FONTS.body, fontSize: '14px', fontWeight: 600, color: colors.text }}>Cadence</div>
          <div style={{ fontFamily: 'monospace', fontSize: '11px', color: colors.textSecondary, marginTop: '2px' }}>cadence · v4</div>
        </div>
      </div>
      <div className="vx-desktop-only vx-table-wrap" style={{ borderRadius: '10px', border: `1px solid ${colors.border}` }}>
        <p style={{ padding: '16px', fontFamily: FONTS.body, color: colors.text }}>Cadence · cadence · v4</p>
      </div>
    </div>
  );
}

/** App deploy audit table. */
export function PreviewAudit(): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  return (
    <div>
      <PreviewTitle label="App Deploy Audit Log" />
      <AuditLogTable items={SAMPLE_AUDIT} colors={colors} />
    </div>
  );
}
